/**
 * Formatação de tela, em pt-BR.
 *
 * `tempoRelativo` juntou três escadas diferentes que respondiam coisas
 * diferentes para a mesma idade: um minuto atrás lia "há instantes" na tela de
 * máquinas e "agora" na de saúde, e a partir de um dia cada uma parava num
 * lugar — data e hora completas numa, "há N dias" noutra, só a data na
 * terceira. Nada disso era decisão: era três pessoas escrevendo a mesma função.
 */

/** Há quanto tempo, em degraus que uma pessoa lê sem contar. */
export function tempoRelativo(iso: string | null | undefined, vazio = 'nunca'): string {
  if (!iso) return vazio;

  const quando = new Date(iso);
  const ms = quando.getTime();
  if (Number.isNaN(ms)) return vazio;

  const minutos = Math.round((Date.now() - ms) / 60_000);
  // Relógio adiantado no servidor não deve virar "há -3 min".
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.round(minutos / 60);
  if (horas < 24) return `há ${horas} h`;

  const dias = Math.round(horas / 24);
  if (dias < 7) return `há ${dias} dia${dias === 1 ? '' : 's'}`;

  // Passou de uma semana: a data diz mais que a contagem.
  return quando.toLocaleDateString('pt-BR');
}

/**
 * Quanto falta até um instante, em degraus que cabem ao lado de uma barra:
 * `2 d 4 h`, `3 h 10 min`, `12 min`.
 *
 * `null` quando o instante já passou ou não se lê. Um reinício no passado não
 * é "reinicia em 0 min": é dado velho, e quem mostra decide como dizer isso.
 */
export function tempoAte(iso: string | null | undefined, agora = Date.now()): string | null {
  if (!iso) return null;
  const alvo = Date.parse(iso);
  if (Number.isNaN(alvo) || alvo <= agora) return null;

  const minutos = Math.floor((alvo - agora) / 60_000);
  if (minutos < 1) return 'menos de 1 min';
  if (minutos < 60) return `${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) {
    const resto = minutos % 60;
    return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
  }

  const dias = Math.floor(horas / 24);
  const resto = horas % 24;
  return resto === 0 ? `${dias} d` : `${dias} d ${resto} h`;
}

/**
 * Uma fração 0..1 como porcentagem inteira, do jeito que o `/usage` mostra.
 *
 * O que é maior que zero e arredondaria para zero vira `<1%`: "0%" diria que
 * não se gastou nada, e gastou-se pouco. Zero de verdade continua `0%`.
 */
export function porcento(fracao: number): string {
  if (fracao > 0 && fracao < 0.005) return '<1%';
  return `${Math.round(fracao * 100)}%`;
}

/** Tokens na escala em que se comparam: `412 mil`, `1 mi`, `1,5 mi`. */
export function tokensCurtos(n: number): string {
  if (n < 1_000) return String(n);
  const mil = Math.round(n / 1_000);
  if (mil < 1_000) return `${mil} mil`;
  const mi = Math.round(n / 100_000) / 10;
  return `${mi.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
}

/** Extensão em minúsculas, sem o ponto. Vazio quando não há. */
export function extensaoDe(caminho: string): string {
  const nome = caminho.split(/[\\/]/).pop() ?? '';
  const ponto = nome.lastIndexOf('.');
  return ponto > 0 ? nome.slice(ponto + 1).toLowerCase() : '';
}

/** Um decimal com vírgula, sem o `,0` que não diz nada: `4,8`, `5`. */
function umDecimal(n: number): string {
  return n.toFixed(1).replace('.', ',').replace(/,0$/, '');
}

/**
 * Bytes na escala em que se lê: `22 B`, `850 kB`, `2,3 MB`, `4,8 GB`.
 *
 * Base 1024 com o rótulo curto de sempre — é a régua do disco e do sistema de
 * arquivos, a mesma em que o teto de anexo (`5 GiB`) foi escrito. Vírgula
 * decimal como o resto do painel (`1,5 mi`); até 2026-10-31 saía `2.3 MB`, o
 * único número da tela com ponto. A conta passa para a unidade seguinte quando
 * o ARREDONDADO chega a 1024, senão `1023,97 MB` saía como `1024 MB`.
 */
export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (Math.round(kb) < 1024) return `${Math.round(kb)} kB`;
  const mb = kb / 1024;
  if (Math.round(mb * 10) / 10 < 1024) return `${umDecimal(mb)} MB`;
  return `${umDecimal(mb / 1024)} GB`;
}

/** O ritmo de um envio: `12,4 MB/s`. */
export function velocidadeLegivel(bytesPorSegundo: number): string {
  return `${tamanhoLegivel(Math.round(bytesPorSegundo))}/s`;
}

/**
 * Uma duração curta para ao lado de uma barra: `45 s`, `3 min 20 s`, `12 min`,
 * `2 h 5 min`.
 *
 * Acima de um minuto os segundos andam de 5 em 5: uma estimativa que troca de
 * dígito a cada tique parece mais precisa do que é. E passando de um dia ela
 * vira `mais de 24 h` — a conta nessa escala é chute, e dizer "31 h" com
 * convicção seria inventar precisão.
 */
export function duracaoLegivel(segundos: number): string {
  const total = Math.max(1, Math.ceil(segundos));
  if (total < 60) return `${total} s`;
  // 59 min 58 s arredonda para 3600: passa direto à escala das horas, em vez de
  // dizer `60 min`.
  const arredondado = Math.round(total / 5) * 5;
  if (arredondado < 3_600) {
    const min = Math.floor(arredondado / 60);
    const resto = arredondado % 60;
    return min < 10 && resto > 0 ? `${min} min ${resto} s` : `${min} min`;
  }
  const minutos = Math.round(total / 60);
  if (minutos >= 24 * 60) return 'mais de 24 h';
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
}
